import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useLegalMatterPracticeAreaAutocompleteQuery } from '../../../../../queries';
import { Checkbox } from '../../../Checkbox';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { styleSheet } from './styles';

export const MatterPracticeAreaFilterDropdown = ({
	selectedPracticeAreas,
	setSelectedPracticeAreas,
	anchorClassName,
}: {
	selectedPracticeAreas: Api.ILegalMatterPracticeArea[];
	setSelectedPracticeAreas: React.Dispatch<React.SetStateAction<Api.ILegalMatterPracticeArea[]>>;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const practiceAreaAutocompleteQuery = useLegalMatterPracticeAreaAutocompleteQuery({
		enabled: true,
		pageSize: 25,
		fragment: '',
	});

	const practiceAreas = React.useMemo(() => {
		return practiceAreaAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [practiceAreaAutocompleteQuery.data]);

	const dropdownMessage = React.useMemo(() => {
		if (selectedPracticeAreas.length === 0) {
			return 'Practice Area';
		}
		return selectedPracticeAreas.length === 1
			? selectedPracticeAreas[0]?.name
			: `${selectedPracticeAreas[0]?.name} +${selectedPracticeAreas.length - 1}`;
	}, [selectedPracticeAreas]);

	const resetFilter = () => {
		setSelectedPracticeAreas([]);
	};

	const isPracticeAreaSelected = (practiceArea: Api.ILegalMatterPracticeArea) => {
		return selectedPracticeAreas.some(p => p.id === practiceArea.id);
	};

	const onTogglePracticeArea = (practiceArea: Api.ILegalMatterPracticeArea) => {
		const isSelected = isPracticeAreaSelected(practiceArea);

		if (isSelected) {
			setSelectedPracticeAreas(selectedPracticeAreas.filter(p => p.id !== practiceArea.id));
		} else {
			setSelectedPracticeAreas([...selectedPracticeAreas, practiceArea]);
		}
	};

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<span>{dropdownMessage}</span>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{practiceAreas.map(practiceArea => {
						return (
							<div className={css(styleSheet.dropdownRow)} key={`practice-area-select-${practiceArea.id}`}>
								<Checkbox
									type='large'
									checked={isPracticeAreaSelected(practiceArea)}
									id={practiceArea.id}
									onChange={() => onTogglePracticeArea(practiceArea)}
								/>
								<span>{practiceArea.name}</span>
							</div>
						);
					})}
				</div>

				{practiceAreaAutocompleteQuery.hasNextPage && !practiceAreaAutocompleteQuery.isFetchingNextPage && (
					<Waypoint onEnter={() => practiceAreaAutocompleteQuery.fetchNextPage()} />
				)}
				<div className={css(dropdownStyles.dropdownSeparator)} />

				<button onClick={resetFilter}>Reset Filter</button>
			</div>
		</Dropdown>
	);
};
